<template>
    <div id="qpe_histogram"
        ref="histogram"
        :style="css_props"
    >
        <div :id="id"
            :style="{'text-align': 'center'}"
        />
    </div>
</template>

<script>
    import { 
        get_diagram_shape,
        init_chart_resize_observer
    } from '@/utils/chart/common.js'
    import { 
        get_container_instance,
        draw_background_for_screenshot,
    } from '@/utils/chart/background.js'
    import {
        draw_x_axis_label,
        get_scale_time,
        draw_x_axis_time_tick,
        draw_day_line,
    } from '@/utils/chart/x_axis.js'
    import {
        draw_y_axis_label,
        draw_y_axis2_label,
        get_scale_y,
        draw_y_axis_tick,
        draw_y_axis2_tick,
        draw_y_axis_basic_background,
    } from '@/utils/chart/y_axis.js'
    import { draw_cross } from '@/utils/chart/graph.js'
    import { draw_tooltip } from '@/utils/chart/tooltip.js'
    import { judge_value_valid } from '@/utils/chart/verification.js'
    import { take_screenshot } from '@/utils/common/screenshot.js'
    import { mapState } from 'vuex'
    import * as d3 from 'd3';

    export default {
        name: 'QpeHistogram',
               
        props: {
            // 同一頁面超過一個 d3 圖時就需要用到
            id: {
                type: String,
                default() {
                    return 'unique_chart'
                }
            },
            // 原始資料 跟下面那個擇一填
            choosed_time_series_data: {
                type: Array,
                default () {
                    return []
                }
            },
            choosed_band_data: {
                type: Array,
                default () {
                    return []
                }
            },
            // 只有rainfall會畫這個
            choosed_time_series_line_data: {
                type: Array,
                default () {
                    return []
                }
            },
            // 選擇測站的時間 跟下面那個擇一個填
            x_axis_time_series_data: {
                type: Array,
                default () {
                    return []
                }
            },
            // 選擇要展示的測站
            x_axis_band_data: {
                type: Array,
                default () {
                    return []
                }
            },
            basic_layout_setting: {
                type: Object,
                default () {
                    // margin.left 不能小於 25
                    return {
                        'height': 500,
                        'margin': {
                            'top': 80,
                            'right': 50,
                            'bottom': 45,
                            'left': 30,
                        }
                    }
                }
            },
            // 直方圖主表題 必填
            svg_main_title: {
                type: String,
                default () {
                    return '我是 props 主標題'
                }
            },
            // 直方圖副標題 必填
            svg_sub_title: {
                type: String,
                default () {
                    return '我是 props 副標題'
                }
            },
            // X軸圖說
            // X軸 ticks *不是數字打幾就會出現幾個 tick
            // X軸 display_interval 每幾個 tick 會顯示值
            x_axis: {
                type: Object,
                default () {
                    return {
                        'label': '我是 props X軸圖說',
                        'ticks': 48,
                        'display_interval': 4,
                        'label_distance': 40
                    }
                }
            },
            // y軸圖說
            // y軸 ticks * 不是數字打幾就會出現幾個間隔
            y_axis: {
                type: Object,
                default () {
                    return {
                        'label': '我是 props Y軸圖說',
                        'ticks': 10,
                        'display_interval': 1,
                        'max_value': 100,
                        'min_value': 0,
                        'label_distance': 40
                    }
                }
            },
            // 第二y軸圖說 在右邊
            y_axis2: {
                type: Object,
                default () {
                    return {
                        'label': '我是 props 第二Y軸圖說',
                        'ticks': 10,
                        'display_interval': 1,
                        'max_value': 100,
                        'min_value': 0,
                        'label_distance': 40
                    }
                }
            },
            // 跨日線
            show_day_line: {
                type: Boolean,
                default () {
                    return true
                }
            },
            // 顏色分階 (灰灰白白那個)
            show_tick_color: {
                type: Boolean,
                default () {
                    return true
                }
            },
            // 標示資料結束時間
            show_data_end_time: {
                type: Boolean,
                default () {
                    return true
                }
            },
            draw_legend_function: {
                type: Function,
                default () {
                    return function() {}
                }
            },
            draw_custom_function: {
                type: Function,
                default() {
                    return () => {}
                }
            },
            is_wrap_tick: {
                type: Boolean,
                default() {
                    return false
                }
            },

            tooltip_value_text: {
                type: String,
                default() {
                    return this.$t('__chart_common.tooltip_value')
                }
            },

            tooltip_value_unit: {
                type: String,
                default() {
                    return ''
                }
            },

            show_cwa_logo: {
                type: Boolean,
                default() {
                    return true
                }
            
            }
        },

        data() {
            return {
                width: 0,
                height: 0,
                resize_observer: null,
            }
        },

        computed: {
            css_props() {
                return {
                    '--height': this.basic_layout_setting.height ? 'auto' : '30vh'
                }
            },
            
            diagram_shape() {
                return get_diagram_shape(this.choosed_time_series_data)
            },
        },

        created() {
            this.$nextTick(() => { 
                this.resize_observer = !this.resize_observer && init_chart_resize_observer.call(this, this.$refs.histogram, this.basic_layout_setting.margin)
                this.resize_observer.observe(document.querySelector('.display-block__wrapper'))
            })
        },

        beforeDestroy() {
            this.resize_observer.unobserve(this.$refs.histogram)
        },

        methods: {
            rerender_svg() {
                d3.selectAll(`#${this.id} > svg`).remove();
                d3.selectAll(`#${this.id} > .tooltip`).remove();
                d3.select(`#${this.id} > .drag_svg`)
                    .on('start', null)
                    .on('drag', null)
                    .on('end', null)
                
                this.init_pic()
            },

            init_pic() {
                const common_options = {
                    id: this.id, 
                    width: this.width, 
                    height: this.height, 
                    margin: this.basic_layout_setting.margin,
                }
                const container = get_container_instance(common_options)
                draw_background_for_screenshot(container, common_options)

                const svg = container.append('g')

                const x_axis_label_options = {
                    ...common_options,
                    ...{ x_axis: this.x_axis }
                }
                draw_x_axis_label(svg, x_axis_label_options)

                const y_axis_label_options = {
                    ...common_options,
                    ...{ y_axis: this.y_axis }
                }
                draw_y_axis_label(svg, y_axis_label_options)    

                this.choosed_time_series_data.length !== 0 && this.draw_time_series_data(svg, common_options)
                this.choosed_band_data.length !== 0 && this.draw_station_data(svg, common_options)

                // 需要自己寫圖說時可以參考 chart 的父元件
                this.draw_legend_function(svg, common_options)
            },

            draw_time_series_data(svg, common_options) {
                const scale_time_options = {
                    ...common_options,
                    ...{
                        start_time_str: this.x_axis_time_series_data[0],
                        end_time_str: this.x_axis_time_series_data.slice(-1)[0],
                    }
                }
                const scale_time = get_scale_time(scale_time_options)
                const x_axis_tick_options = {
                    ...common_options,
                    ...{ 
                        x_axis: this.x_axis,
                        scale_time: scale_time,
                    }
                }
                draw_x_axis_time_tick(svg, x_axis_tick_options)

                const scale_y_options = {
                    ...common_options,
                    ...{ y_axis: this.y_axis }
                }
                const scale_y = get_scale_y(scale_y_options)
                
                const y_axis_tick_options = {
                    ...scale_y_options,
                    ...{
                        scale: scale_y
                    }
                }
                draw_y_axis_tick(svg, y_axis_tick_options)
                
                this.show_tick_color && draw_y_axis_basic_background(svg, common_options)
                this.show_day_line && draw_day_line(svg, { ...common_options, ...{ scale_time } })

                // 內容
                const { width, height, margin } = common_options
                const data_length = this.choosed_time_series_data.length
                const scale_data = d3.scaleLinear()
                    .domain([0, data_length])
                    .range([0, width * data_length / (data_length-1)])

                const MARGIN_VALUE = 0.1

                //柱狀圖
                svg.selectAll('.bar')
                    .data(this.choosed_time_series_data)
                    .enter()
                    .append('rect')
                    .attr('fill', (d) => {
                        return judge_value_valid(d) ? 'steelblue' : 'transparent'
                    })
                    .attr('x', (d, i) => {
                        const basic_x_distance = margin.left + scale_data(i)
                        return i === 0 ? basic_x_distance : basic_x_distance - scale_data(1)/2 + scale_data(MARGIN_VALUE)
                    })
                    .attr('y', (d) => {
                        switch (this.diagram_shape) {
                        case 'down_to_up':
                            return judge_value_valid(d) ? scale_y(d) : scale_y(this.y_axis.min_value + 0.25)
                        case 'up_to_down':
                            return scale_y(this.y_axis.max_value)
                        case 'from_center':
                            if (d > 0) {
                                return scale_y(d)
                            }else if(d < 0){
                                return scale_y(0)
                            }
                            return 49.5*height/100
                        }
                    })
                    .attr('width', (d, i, array) => {
                        const is_first = i === 0
                        const is_latest = i === array.length - 1
                        return (is_first || is_latest) ? scale_data(0.5 - MARGIN_VALUE) : scale_data(1 - MARGIN_VALUE*2)
                    })
                    .attr('height', (d) => {
                        switch (this.diagram_shape) {
                        case 'down_to_up':
                            return judge_value_valid(d) ? height - scale_y(d) : height - scale_y(this.y_axis.min_value + 0.25)
                        case 'up_to_down':
                            return judge_value_valid(d) ? scale_y(d) : scale_y(this.y_axis.max_value - 0.25)
                        case 'from_center':
                            if (d > 0) {
                                return height/2 - scale_y(d)
                            } else if( d < 0){
                                return scale_y(d) - height/2
                            }
                            return height/50
                        }
                    })

                //rainfall要多畫一條折線
                this.choosed_time_series_line_data.length !== 0 && this.draw_time_series_line_data(svg, common_options)

                const cross_options = {
                    ...common_options,
                    ...{
                        data: this.choosed_time_series_data,
                        scale_data,
                        scale_y,
                    }
                }
                draw_cross(svg, cross_options)
                
                this.draw_custom_function(svg, width, scale_time, scale_y)

                // 滑鼠覆蓋時的資料提示
                draw_tooltip({
                    basic: {
                        svg,
                        id: this.id,
                        scale_data, 
                        scale_time,
                        scale_y,
                        height: this.height, 
                        margin: this.basic_layout_setting.margin
                    },
                    tooltip: {
                        text: this.tooltip_value_text,
                        unit: this.tooltip_value_unit,
                        no_data_text: this.$t('__chart_common.no_data')
                    },
                    data: {
                        content: this.choosed_time_series_data,
                        x_axis: this.x_axis_time_series_data,
                    },
                    //第二組資料for累積降雨量(需共用X軸), 暫時先寫死
                    extra_data: this.choosed_time_series_line_data.length !== 0 ? {
                        text: '累積降雨量',
                        unit: 'mm',
                        content: this.choosed_time_series_line_data,
                    } : null
                })
            },

            draw_time_series_line_data(svg, common_options) {
                const scale_y_options = {
                    ...common_options,
                    ...{ y_axis: this.y_axis2 }
                }
                
                draw_y_axis2_label(svg, scale_y_options)            
                const scale_y = get_scale_y(scale_y_options)
                
                const y_axis_tick_options = {
                    ...scale_y_options,
                    ...{
                        scale: scale_y
                    }
                }

                draw_y_axis2_tick(svg, y_axis_tick_options)
                
                // 內容
                const { width, height, margin } = common_options
                const data_length = this.choosed_time_series_line_data.length
                const scale_data = d3.scaleLinear()
                    .domain([0, data_length])
                    .range([0, width * data_length / (data_length - 1)])

                //折線圖
                const parsed_data = this.choosed_time_series_line_data.map(d => parseFloat(d));
                svg.append('path')
                    .datum(parsed_data) // 使用整個資料陣列
                    .attr('fill', 'none') // 不填充
                    .attr('stroke', 'steelblue') // 折線顏色
                    .attr('stroke-width', 2)// 線條寬度
                    .attr('d', d3.line()
                        .defined((d) => !isNaN(d)) // 定義有效數據點，忽略 NaN
                        .x((d, i) => {
                            const x = margin.left + scale_data(i);
                            return isNaN(x) ? 0 : x; // 防止 NaN
                        })
                        .y((d) => {
                            const y = scale_y(d);
                            return isNaN(y) ? null : y; // 防止 NaN
                        })
                    );

            },

            draw_station_data(svg, common_options) {
                const width = this.width
                const height = this.height
                const margin = this.basic_layout_setting.margin

                const scale_x = d3.scaleBand()
                    .domain(this.x_axis_band_data)
                    .rangeRound([0, width])
                    .padding(0.1)

                svg.append('g')
                    .attr('transform', `translate(${margin.left}, ${height})`)
                    .call(d3.axisBottom(scale_x))

                if(this.is_wrap_tick) {   
                    const { y } = common_options
                    svg.selectAll(".tick text")
                        .each(function(word) {
                            // 不能 arrow function
                            d3.select(this).text('')
                            const letters = word.split('')
                            const dy = parseFloat(d3.select(this).attr("dy"))
                            const lineHeight = 0.25
                            letters.forEach((letter, index) => {
                                d3.select(this)
                                    .append("tspan")
                                    .attr("x", 0)
                                    .attr("y", y)
                                    .attr("dy", (index+0) * lineHeight + dy + "em")
                                    .style('font-size', "0.75em")
                                    .text(letter)
                            })
                        })
                }

                // 畫 Y 軸
                const scale_y = d3.scaleLinear()
                    .domain([this.y_axis.min_value, this.y_axis.max_value])
                    // 可以反轉 tick 刻度值
                    .range([height, 0])
                
                svg.append('g')
                    .attr('id', 'Y_AXIS')
                    .attr('transform', `translate(${margin.left}, 0)`)
                    .call(d3.axisLeft(scale_y)
                        .tickFormat((d, i) => i % this.y_axis.display_interval === 0 ? d : '')
                        .ticks(this.y_axis.ticks)
                    )

                this.show_tick_color && draw_y_axis_basic_background(svg, common_options)

                // 本來會沒有生成 dom 從 selectAll('rect') 改成 selectAll('.bar') 就有了
                svg.selectAll('.bar')
                    .data(this.choosed_band_data)
                    .enter()
                    .append('rect')
                    .attr('fill', 'steelblue')
                    // 從左到右
                    .attr('x', (d, i) => margin.left + scale_x(this.x_axis_band_data[i]))
                    // 從上到下
                    .attr('y', (d) => scale_y(d))
                    // width 根據 x軸 不然沒辦法顯示正確資料
                    .attr('width', scale_x.bandwidth())
                    .attr('height', (d) => judge_value_valid(d) ? height - scale_y(d) : 0)

                // this._draw_tooltip({svg, scale_data: scale_x, y, tooltip})
            },

            save_png(file_name) {
                const node = document.querySelector('#base-popup__capture_range')
                take_screenshot(node, file_name)
            },
        }
    }
</script>

<style lang="scss">
    #qpe_histogram {
        position: relative;
        height: var(--height);
        margin: 0 auto;
        padding-left: 0.25rem;
        box-sizing: border-box;
        
        text {
            font-size: 15px;
        }
    
        .tooltip {
            position: absolute;
            padding: 0.125rem 0.25rem;
            background: white;
            font: 12px sans-serif;
            text-align: left;
            border-radius: 2px;
            pointer-events: none;
            box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.4);
            white-space: nowrap;
        }

        .vertical_text {
            -webkit-writing-mode: vertical-lr;
            writing-mode: vertical-lr;
        }
        .date_line line{
            stroke: #ece8f7;
            stroke-width: 2;
            stroke-dasharray: 7;
        }

        .date_line path{
            display: none;
        }

        .date_line text {
            opacity: 0.85; fill: rgb(192, 192, 192);
        }

        .main_title {
            fill: rgb(97, 130, 181); 
            font-weight: bold; 
            font-family: 微軟正黑體;
        }

        .title_font_size {
            font-size: 18px;
        }
    }
</style>
